import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import EmployeeDetail from './pages/EmployeeDetail';
import Attendance from './pages/Attendance';
import Leaves from './pages/Leaves';
import Payroll from './pages/Payroll';
import Expenses from './pages/Expenses';
import Companies from './pages/Companies';
import Settings from './pages/Settings';
import SalaryConfig from './pages/SalaryConfig';
import Reminders from './pages/Reminders';
import MyOnboarding from './pages/MyOnboarding';
import Onboarding from './pages/Onboarding';
import CalendarPage from './pages/CalendarPage';
import UserAdmin from './pages/UserAdmin';
import AuditLogs from './pages/AuditLogs';
import MyDashboard from './pages/MyDashboard';
import MyAttendance from './pages/MyAttendance';
import MyLeave from './pages/MyLeave';
import MyPayroll from './pages/MyPayroll';
import MyExpenses from './pages/MyExpenses';
import MyDocuments from './pages/MyDocuments';
import MyProfile from './pages/MyProfile';
import TeamApprovals from './pages/TeamApprovals';
import RolePermissions from './pages/RolePermissions';
import BulkSalaryRevision from './pages/BulkSalaryRevision';
import AttendanceCorrections from './pages/AttendanceCorrections';
import Assets from './pages/Assets';
import Training from './pages/Training';
import Performance from './pages/Performance';
import Analytics from './pages/Analytics';
import MyDevelopment from './pages/MyDevelopment';
import EmailConfig from './pages/EmailConfig';
import Shifts from './pages/Shifts';
import Geofences from './pages/Geofences';
import AttendanceSettings from './pages/AttendanceSettings';
import LetterheadSettings from './pages/LetterheadSettings';
import OfferLetterGenerator from './pages/OfferLetterGenerator';
import SalaryCertificateGenerator from './pages/SalaryCertificateGenerator';
import HRDocumentsHub from './pages/HRDocumentsHub';
import MyResignation from './pages/MyResignation';
import ExitManagement from './pages/ExitManagement';
import Offboarding from './pages/Offboarding';
import DataMigration from './pages/DataMigration';
import LeavePolicies from './pages/LeavePolicies';
import DeviceTrust from './pages/DeviceTrust';
import MyDevices from './pages/MyDevices';
import Holidays from './pages/Holidays';
import WorkflowTemplates from './pages/WorkflowTemplates';
import MyWFH from './pages/MyWFH';
import WFHAdmin from './pages/WFHAdmin';
import MyOD from './pages/MyOD';
import ODAdmin from './pages/ODAdmin';
import LeaveAnalytics from './pages/LeaveAnalytics';
import AttendanceReport from './pages/AttendanceReport';
import HrReportPage from './pages/HrReportPage';
import HelpCenter from './pages/HelpCenter';
import Layout from './components/Layout';
import OnboardingGate from './components/OnboardingGate';
import ForcePasswordChange from './components/ForcePasswordChange';
import DeviceVerifier from './components/DeviceVerifier';
import './App.css';

// Auth Context
const AuthContext = createContext(null);

// Role guard wrapper — redirects to / when user role isn't in allowed list
const RoleRoute = ({ allowed, children }) => {
  const ctx = useContext(AuthContext);
  if (ctx?.user && !allowed.includes(ctx.user.role)) return <Navigate to="/" replace />;
  return children;
};

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, [token]);

  const login = (userData, accessToken) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('theme');
    return stored || 'light';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Redirects employees to portal home, others get the admin dashboard
const HomeRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'employee') return <Navigate to="/me" replace />;
  return <Dashboard />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-background">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/onboarding/me" element={
                <ProtectedRoute>
                  <MyOnboarding />
                </ProtectedRoute>
              } />
              <Route path="/" element={
                <ProtectedRoute>
                  <OnboardingGate>
                    <>
                      <ForcePasswordChange />
                      <DeviceVerifier />
                      <Layout />
                    </>
                  </OnboardingGate>
                </ProtectedRoute>
              }>
                <Route index element={<HomeRouter />} />
                <Route path="employees" element={<Employees />} />
                <Route path="employees/:id" element={<EmployeeDetail />} />
                <Route path="onboarding" element={<Onboarding />} />
                <Route path="attendance" element={<Attendance />} />
                <Route path="leaves" element={<Leaves />} />
                <Route path="payroll" element={<Payroll />} />
                <Route path="expenses" element={<Expenses />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="companies" element={<Companies />} />
                <Route path="salary-config" element={<SalaryConfig />} />
                <Route path="reminders" element={<Reminders />} />
                <Route path="users" element={<UserAdmin />} />
                <Route path="role-permissions" element={<RolePermissions />} />
                <Route path="bulk-salary-revision" element={<BulkSalaryRevision />} />
                <Route path="attendance-corrections" element={<AttendanceCorrections />} />
                <Route path="audit-logs" element={<AuditLogs />} />
                <Route path="assets" element={<Assets />} />
                <Route path="training" element={<Training />} />
                <Route path="performance" element={<Performance />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="email-config" element={<EmailConfig />} />
                <Route path="shifts" element={<Shifts />} />
                <Route path="geofences" element={<Geofences />} />
                <Route path="attendance-policy" element={<RoleRoute allowed={['super_admin', 'hr_admin']}><AttendanceSettings /></RoleRoute>} />
                <Route path="letterhead/:companyId" element={<RoleRoute allowed={['super_admin', 'hr_admin']}><LetterheadSettings /></RoleRoute>} />
                <Route path="hr-documents/offer-letter" element={<RoleRoute allowed={['super_admin', 'hr_admin']}><OfferLetterGenerator /></RoleRoute>} />
                <Route path="hr-documents/salary-certificate" element={<RoleRoute allowed={['super_admin', 'hr_admin']}><SalaryCertificateGenerator /></RoleRoute>} />
                <Route path="hr-documents/templates" element={<RoleRoute allowed={['super_admin', 'hr_admin']}><HRDocumentsHub /></RoleRoute>} />
                <Route path="exit-management" element={<RoleRoute allowed={['super_admin', 'hr_admin', 'manager']}><ExitManagement /></RoleRoute>} />
                <Route path="offboarding" element={<RoleRoute allowed={['super_admin', 'hr_admin', 'manager', 'payroll_admin']}><Offboarding /></RoleRoute>} />
                <Route path="data-migration" element={<RoleRoute allowed={['super_admin']}><DataMigration /></RoleRoute>} />
                <Route path="leave-policies" element={<RoleRoute allowed={['super_admin', 'hr_admin']}><LeavePolicies /></RoleRoute>} />
                <Route path="device-trust" element={<RoleRoute allowed={['super_admin', 'hr_admin']}><DeviceTrust /></RoleRoute>} />
                <Route path="holidays" element={<RoleRoute allowed={['super_admin', 'hr_admin']}><Holidays /></RoleRoute>} />
                <Route path="workflows" element={<RoleRoute allowed={['super_admin', 'hr_admin']}><WorkflowTemplates /></RoleRoute>} />
                <Route path="wfh" element={<RoleRoute allowed={['super_admin', 'hr_admin', 'manager']}><WFHAdmin /></RoleRoute>} />
                <Route path="od" element={<RoleRoute allowed={['super_admin', 'hr_admin', 'manager']}><ODAdmin /></RoleRoute>} />
                <Route path="leave-analytics" element={<RoleRoute allowed={['super_admin', 'hr_admin', 'payroll_admin']}><LeaveAnalytics /></RoleRoute>} />
                <Route path="reports/attendance" element={<RoleRoute allowed={['super_admin', 'hr_admin', 'manager']}><AttendanceReport /></RoleRoute>} />
                <Route path="reports/leaves" element={<RoleRoute allowed={['super_admin', 'hr_admin', 'manager']}><HrReportPage type="leave" /></RoleRoute>} />
                <Route path="reports/expenses" element={<RoleRoute allowed={['super_admin', 'hr_admin', 'payroll_admin', 'manager']}><HrReportPage type="expense" /></RoleRoute>} />
                <Route path="me/resignation" element={<MyResignation />} />
                <Route path="settings" element={<Settings />} />
                {/* Employee Self-Service Portal */}
                <Route path="me" element={<MyDashboard />} />
                <Route path="me/attendance" element={<MyAttendance />} />
                <Route path="me/leave" element={<MyLeave />} />
                <Route path="me/payroll" element={<MyPayroll />} />
                <Route path="me/expenses" element={<MyExpenses />} />
                <Route path="me/documents" element={<MyDocuments />} />
                <Route path="me/profile" element={<MyProfile />} />
                <Route path="me/development" element={<MyDevelopment />} />
                <Route path="me/devices" element={<MyDevices />} />
                <Route path="me/wfh" element={<MyWFH />} />
                <Route path="me/od" element={<MyOD />} />
                <Route path="help" element={<HelpCenter />} />
                {/* Manager Self-Service */}
                <Route path="my-team" element={<TeamApprovals />} />
              </Route>
            </Routes>
            <Toaster position="top-right" richColors />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
